import { defineStore } from 'pinia';

import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';
import { formatMetricRequestDate, formatMetricDisplayDate } from '@/helpers/date-formatter';

const METRIC_URL = 'v2/dashboard/metrics';
const DATASOURCE_METRIC_URL = 'v2/datasource/metric';

export const useMetricStore = defineStore('metric', {
    state: () => ({
        metric: {
            company_id: null,
            name: null,
            description: null,
            display_format: 'table',
            start_date: null,
            end_date: null,
            sharable: false,
            company_pool_id: null,
            groups: [],
            data: [],
        },
        editMetric: {},
        metrics: [],
        companyPools: [],
        totalRecords: null,
        attributesGroupedByBlocks: {
            groupings: [],
            metrics: [],
        },
    }),
    actions: {
        async fetchAllMetrics(params) {
            try {
                const response = await GlobalRequest.getRequest(METRIC_URL, {
                    params: {
                        company_id: params.company_id,
                        page_size: params.rows ?? 10,
                        // params page starts from 0 so we are adding one everytime
                        page: params.page ? params.page + 1 : 1,
                    },
                });
                if (response.statusCode == 200) {
                    // mapping filter information and users count as a single entity before loading into the array
                    this.metrics = response.data;
                    this.totalRecords = response.data?.length;
                    return this.metrics;
                }
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to fetch all metrics');
            }
        },
        // DataSource Metrics
        async fetchAllDSMetrics(params) {
            try {
                const response = await GlobalRequest.getRequest(DATASOURCE_METRIC_URL, {
                    params: {
                        ...params,
                        page_size: params.rows ?? 10,
                        page: params.page ? params.page + 1 : 1,
                    },
                });
                if (response.statusCode == 200) {
                    // mapping filter information and users count as a single entity before loading into the array
                    this.metrics = params.paginate ? response.data?.data : response.data;
                    this.totalRecords = params.paginate ? response.data?.total : response.data?.length;
                    // Format metrics objects
                    return this.metrics;
                }
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to fetch all metrics');
            }
        },

        async fetchAllCompanyPools(companyId) {
            try {
                const response = await GlobalRequest.getRequest(`${METRIC_URL}/pool?company_id=${companyId}`);
                if (response.statusCode == 200) {
                    // mapping filter information and users count as a single entity before loading into the array
                    this.companyPools = response.data;
                    return this.companyPools;
                }
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to fetch all company pools');
            }
        },
        async fetchMetricDetails(company_id, id) {
            try {
                const response = await GlobalRequest.getRequest(METRIC_URL + '/' + id, {
                    params: {
                        company_id: company_id,
                    },
                });
                if (response.statusCode == 200) {
                    // load editresponse after creation of basic metric
                    // the information required is inside filter
                    this.loadEditMetric(response.data);
                }
                return response.data;
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to fetch metric by id');
            }
        },
        async createMetric(body) {
            try {
                const sharable = body.sharable ? 1 : 0;
                if (sharable === 0) {
                    body.company_pool_id = null;
                }
                const response = await GlobalRequest.postRequest(METRIC_URL, {
                    ...body,
                    sharable,
                });
                if (response.statusCode == '201') showToast(response, 'Metric created successfully');
                // load editMetric after creation of basic metric
                this.loadEditMetric(response.data);
                return response.data;
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to create new metric');
            }
        },
        async updateMetric(metricId, body) {
            try {
                // changing date format before making an request in the api
                body.start_date = body.start_date ? formatMetricRequestDate(body.start_date) : null;
                body.end_date = body.end_date ? formatMetricRequestDate(body.end_date) : null;
                body.sharable = body.sharable ? 1 : 0;
                const response = await GlobalRequest.patchRequest(METRIC_URL + '/' + metricId, body);
                // at the moment, metrics information is not fetched after it has been updated so
                //  everytime we make any updates, we will fetch metric details by id here
                if (response.statusCode == '200') showToast(response, 'Metric changes saved');
                await this.fetchMetricDetails(body?.company_id, metricId);
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to save metric changes');
            }
        },
        async deleteMetric(metricId, company_id) {
            try {
                const response = await GlobalRequest.deleteRequest(METRIC_URL + '/' + metricId, {
                    params: { company_id },
                });
                if (response.statusCode == '200') showToast(response, 'Metric deleted');
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Failed to delete metric');
            }
        },
        async loadEditMetric(metricData) {
            this.editMetric = {
                company_id: metricData.company_id,
                name: metricData.name,
                description: metricData.description,
                display_format: metricData.display_format,
                start_date: metricData.start_date ? formatMetricDisplayDate(metricData.start_date) : null,
                end_date: metricData.end_date ? formatMetricDisplayDate(metricData.end_date) : null,
                data: metricData.data,
                groups: metricData.group_by,
                sharable: metricData.sharable === 1 ? true : false,
                company_pool_id: metricData.company_pool_id ?? null,
            };
        },
        initMetric() {
            this.metric.company_id = null;
            this.metric.name = null;
            this.metric.description = null;
            this.metric.display_format = 'table';
            this.metric.start_date = null;
            this.metric.end_date = null;
            this.metric.sharable = false;
            this.metric.company_pool_id = null;
            this.metric.groups = [];
            this.metric.data = [];
        },
        flushMetricData() {
            this.editMetric = {};
            this.metrics = [];
            this.totalRecords = null;
            this.attributesGroupedByBlocks = {
                groupings: [],
                metrics: [],
            };
        },
    },
    getters: {
        allDSMetrics() {
            return this.metrics
                ?.map((metricObj) => metricObj.metric)
                ?.map((metricObj) => {
                    return {
                        ...metricObj,
                        metricName: `${metricObj.name} (${metricObj.code})`,
                    };
                });
        },
    },
});
